import { useEffect, useState, useRef } from "react";
import { ArrowLeft, Send, Paperclip, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Sample conversation data for a team channel
const conversationData = [
  {
    id: 1,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "Team, I've been monitoring our top 5 competitors over the past month and have some important updates.",
    timestamp: "Yesterday at 9:15 AM",
    isUser: false
  },
  {
    id: 2,
    sender: "AnalyticsBot",
    avatarSrc: "/crew-agents/webanalytics.png",
    message: "Looking forward to hearing what you've found. I have some engagement analytics that might complement your findings.",
    timestamp: "Yesterday at 9:20 AM",
    isUser: false
  },
  {
    id: 3,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "Here's what I've discovered:\n\n1. Competitor A has launched a new AI-driven content recommendation engine\n2. Competitor B has redesigned their analytics dashboard with improved visualization\n3. Competitor C is beta testing a voice interaction feature",
    timestamp: "Yesterday at 9:25 AM",
    isUser: false
  },
  {
    id: 4,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "What's concerning is that three competitors now offer features similar to our PageMind Analytics module, but with more robust visualization capabilities.",
    timestamp: "Yesterday at 9:27 AM",
    isUser: false
  },
  {
    id: 5,
    sender: "ContentBot",
    avatarSrc: "/crew-agents/content-generator.jpg",
    message: "This aligns with what I'm seeing in industry publications. There's been a 40% increase in mentions of advanced analytics visualizations in the last quarter.",
    timestamp: "Yesterday at 9:35 AM",
    isUser: false
  },
  {
    id: 6,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "Great insights. Can you provide a more detailed comparison of their visualization features vs. ours?",
    timestamp: "Yesterday at 10:15 AM",
    isUser: true
  },
  {
    id: 7,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "I'll prepare a detailed comparison report. Would you like me to focus on technical capabilities, user experience, or both?",
    timestamp: "Yesterday at 10:20 AM",
    isUser: false
  },
  {
    id: 8,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "Both please, and include any pricing differences if you have that data.",
    timestamp: "Yesterday at 10:22 AM",
    isUser: true
  },
  {
    id: 9,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "Working on it now. I'll have the full comparison ready in about 2 hours.",
    timestamp: "Yesterday at 10:25 AM",
    isUser: false
  },
  {
    id: 10,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "I've completed the competitive analysis on analytics visualization features. Here are the key findings:\n\n1. Our competitors offer 3-5 more visualization types than we do\n2. Two competitors have implemented real-time data streaming visualizations\n3. One competitor has added AI-powered insights that automatically highlight unusual patterns\n\nThe full report is available now. Would you like me to prepare a presentation for the product team?",
    timestamp: "Today at 8:30 AM",
    isUser: false
  }
];

// Team members for this channel
const teamMembers = [
  {
    name: "CompetitorBot",
    role: "Competitor Intelligence",
    avatarSrc: "/crew-agents/competitors.png",
    status: "online"
  },
  {
    name: "AnalyticsBot",
    role: "Analytics Specialist",
    avatarSrc: "/crew-agents/webanalytics.png", 
    status: "online"
  },
  {
    name: "ContentBot",
    role: "Content Analyzer",
    avatarSrc: "/crew-agents/content-generator.jpg",
    status: "away"
  },
  {
    name: "You",
    role: "Product Manager",
    avatarSrc: "/crew-agents/page-semantic.png",
    status: "online"
  }
];

const ChatMessage = ({ message }: { message: any }) => {
  const isUser = message.isUser;
  
  return (
    <div className={`flex gap-3 mb-4 ${isUser ? 'justify-end' : ''}`}>
      {!isUser && (
        <img 
          src={message.avatarSrc} 
          alt={message.sender}
          className="h-8 w-8 rounded-full object-cover mt-1"
        />
      )}
      
      <div className={`max-w-[70%] ${isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'} rounded-lg px-4 py-2`}>
        <div className="flex justify-between items-baseline mb-1">
          <span className={`font-medium text-sm ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
            {message.sender}
          </span>
          <span className={`text-xs ml-4 ${isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
            {message.timestamp}
          </span>
        </div>
        <p className={`text-sm whitespace-pre-line ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
          {message.message}
        </p>
      </div>
      
      {isUser && (
        <img 
          src={message.avatarSrc} 
          alt={message.sender}
          className="h-8 w-8 rounded-full object-cover mt-1"
        />
      )}
    </div>
  );
};

const TeamMembersList = ({ members }: { members: any[] }) => {
  return (
    <div className="border rounded-md p-3 bg-background">
      <h3 className="font-medium mb-3 flex items-center">
        <Users className="h-4 w-4 mr-2" />
        Team Members
      </h3>
      <div className="space-y-3">
        {members.map((member, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className="relative">
              <Avatar>
                <AvatarImage src={member.avatarSrc} alt={member.name} />
                <AvatarFallback>{member.name[0]}</AvatarFallback>
              </Avatar>
              <div className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-background ${
                member.status === 'online' ? 'bg-green-500' : 
                member.status === 'away' ? 'bg-yellow-500' : 'bg-gray-400'
              }`} />
            </div>
            <div>
              <div className="text-sm font-medium">{member.name}</div>
              <div className="text-xs text-muted-foreground">{member.role}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CompetitorsChannelPage = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState(conversationData);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    document.title = "Competitors Intel Channel | PageMind Admin";
    
    // Scroll to bottom of messages
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (message.trim()) {
      // Add user message
      const newUserMessage = {
        id: conversation.length + 1,
        sender: "You",
        avatarSrc: "/crew-agents/page-semantic.png",
        message,
        timestamp: "Just now",
        isUser: true
      };
      
      setConversation([...conversation, newUserMessage]);
      setMessage("");
      
      // Simulate response after a delay (in a real app, this would be an API call)
      setTimeout(() => {
        const botResponse = {
          id: conversation.length + 2,
          sender: "CompetitorBot",
          avatarSrc: "/crew-agents/competitors.png",
          message: "I'll prepare that presentation for the product team. Is there any specific area of the competitive analysis you'd like me to emphasize?",
          timestamp: "Just now",
          isUser: false
        };
        
        setConversation(prev => [...prev, botResponse]);
      }, 1000);
    }
  };

  return (
    <div className="container mx-auto p-6 h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 pb-4 border-b">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/insights/channels')}
            className="mr-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center">
            <div className="flex items-center gap-1 bg-primary/10 rounded-full px-3 py-1.5">
              <Users className="h-5 w-5 text-primary" />
              <div>
                <div className="font-bold flex items-center gap-2">
                  Competitors Intel
                  <Badge variant="outline" className="text-xs bg-primary/5">Team Channel</Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <Button variant="outline" size="sm">
            Channel Settings
          </Button>
        </div>
      </div>
      
      {/* Main content - split into chat and sidebar */}
      <div className="flex gap-4 flex-1 overflow-hidden">
        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-background/80 rounded-lg">
          {conversation.map(msg => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0 hidden lg:block">
          <TeamMembersList members={teamMembers} />
        </div>
      </div>
      
      {/* Message Input */}
      <div className="mt-4">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-full"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          
          <Input
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Message #competitors-intel..."
            className="flex-1"
          />
          
          <Button
            type="submit"
            disabled={!message.trim()}
          >
            <Send className="h-4 w-4 mr-2" />
            Send
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CompetitorsChannelPage;